import axios from "axios";

import {
  USER_TOKEN_REFRESH,
  USER_LOGOUT,
  USER_DETAILS_RESET,
  USER_LIST_RESET,
} from "../constants/userConstants";
import {
  AUTH_TOKEN_REFRESH_SUCCESS,
  AUTH_LOGOUT,
} from "../constants/authConstants";
import { API_BASE_URL } from "../api/apiBase";
import { getProductionAuthPersistence } from "../persistence/authPersistenceProduction";
import {
  REFRESH_OUTCOME,
  RefreshSupersededError,
  isRefreshSupersededError,
} from "./refreshErrors";

// Bare axios client — must NOT route through ../api/axios so the refresh call
// itself never re-enters the response interceptor on its own 401.
// Same-origin relative `/api` (not absolute http://hostname) — HTTPS pages
// must not trigger Mixed Content on /token/refresh/.
const refreshApi = axios.create({ baseURL: API_BASE_URL });

let inFlight = null;

// Sticky terminal flag. Set when auth is invalidated by either path:
//   - manual logout (logout thunk calls invalidateAuth before clearing storage)
//   - refresh failure (performTerminalLogout sets it on the way out)
//   - persistence clear failure containment (P-1D-R1)
// Once true, no new refresh may start and any in-flight refresh that resolves
// later refuses to apply its result. Cleared only on full module reload
// (page navigation), which is exactly what performTerminalLogout triggers.
let terminated = false;

function getAuthPersistence() {
  return getProductionAuthPersistence();
}

export function invalidateAuth() {
  terminated = true;
}

export function isAuthInvalidated() {
  return terminated;
}

/**
 * Terminal invalidation for the unchanged current session only.
 * P-1D-R1: clear must succeed before Redux logout dispatches or navigation.
 */
function performTerminalLogout(dispatch) {
  // ⚠ HARD-RELOAD CONTRACT (Phase 4A):
  //   The terminal `window.location.href = "/login"` below is LOAD-BEARING.
  //   AUTH_LOGOUT alone does not reset profile/bootstrap/coordinator-module
  //   state. The page reload discards the entire JS context (resetting
  //   `terminated` and `inFlight` on next module load) and re-hydrates from
  //   empty localStorage. See authReducers.js AUTH_LOGOUT comment for the
  //   full inventory.

  getAuthPersistence().clearUserInfoBlob();

  terminated = true;

  dispatch({ type: USER_LOGOUT });
  dispatch({ type: USER_DETAILS_RESET });
  dispatch({ type: USER_LIST_RESET });
  dispatch({ type: AUTH_LOGOUT });

  window.location.href = "/login";
}

/**
 * True when the persisted session is still the one that initiated refresh.
 */
function isUnchangedCurrentSession(capturedRefresh) {
  const current = getAuthPersistence().readUserInfoBlob();
  if (!capturedRefresh) {
    return !current?.refresh;
  }
  return current?.refresh === capturedRefresh;
}

export function getOrStartRefresh(dispatch) {
  if (terminated) return Promise.reject(new Error("Auth terminated"));

  if (inFlight) return inFlight;

  inFlight = (async () => {
    let capturedRefresh;
    try {
      const stored = getAuthPersistence().readUserInfoBlob();
      capturedRefresh = stored?.refresh;
      if (!capturedRefresh) throw new Error("No refresh token");

      const { data } = await refreshApi.post("/token/refresh/", {
        refresh: capturedRefresh,
      });

      if (terminated) {
        throw new RefreshSupersededError(
          REFRESH_OUTCOME.SUPERSEDED_TERMINATED,
          "Auth terminated during refresh"
        );
      }

      const stillStored = getAuthPersistence().readUserInfoBlob();
      if (!stillStored) {
        throw new RefreshSupersededError(
          REFRESH_OUTCOME.SUPERSEDED_SESSION_CLEAR,
          "Auth removed during refresh"
        );
      }

      if (stillStored.refresh !== capturedRefresh) {
        throw new RefreshSupersededError(
          REFRESH_OUTCOME.SUPERSEDED_ACCOUNT_SWITCH,
          "Auth changed during refresh"
        );
      }

      const access = data.access;

      const next = { ...stillStored, access };
      getAuthPersistence().writeUserInfoBlob(next);

      dispatch({ type: USER_TOKEN_REFRESH, payload: access });
      dispatch({ type: AUTH_TOKEN_REFRESH_SUCCESS, payload: access });

      return access;
    } catch (err) {
      if (isRefreshSupersededError(err)) {
        throw err;
      }

      if (!terminated && isUnchangedCurrentSession(capturedRefresh)) {
        try {
          performTerminalLogout(dispatch);
        } catch (clearErr) {
          terminated = true;
          throw clearErr;
        }
      }

      throw err;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

export function __resetRefreshCoordinator() {
  inFlight = null;
  terminated = false;
}
